import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  useCallback,
} from "react";
import { supabase } from "../supabase";

// Create context
const ProductContext = createContext();

export const normalizeProduct = (product) => {
  const images = Array.isArray(product?.images)
    ? product.images.filter(Boolean)
    : [];
  let media = product?.media || {};

  if (typeof media === "string") {
    try {
      media = JSON.parse(media);
    } catch (_) {
      media = {};
    }
  }

  const featuredImage = media?.featuredImage || images[0] || product?.image || "";
  const gallery = Array.isArray(media?.gallery)
    ? media.gallery
    : images.slice(1);
  const price =
    product?.final_price_toman ?? product?.price_toman ?? product?.price ?? 0;
  const discount = Number(product?.discount || 0);

  return {
    ...product,
    images,
    image: featuredImage,
    media: { ...media, featuredImage, gallery },
    variants: Array.isArray(product?.variants) ? product.variants : [],
    price,
    categoryId: product?.category_id || product?.category_slug || null,
    brandId: product?.brand_id || product?.brand_name || null,
    createdAt: product?.created_at,
    pricing: {
      ...product?.pricing,
      basePrice: {
        ...product?.pricing?.basePrice,
        toman: price,
      },
      discount: {
        ...product?.pricing?.discount,
        active: discount > 0,
        percentage: discount,
      },
    },
  };
};

const normalizeProducts = (products) => (products || []).map(normalizeProduct);

export const PRODUCT_PAGE_SIZE = 24;

export const sanitizeProductSearch = (value = "") =>
  String(value)
    .replace(/[,%_()]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

// Custom hook to use the product context
export const useProducts = () => {
  return useContext(ProductContext);
};

export const ProductProvider = ({ children }) => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch categories from Supabase
  const fetchCategories = async () => {
    try {
      const { data, error } = await supabase
        .from("categories")
        .select("*")
        .eq("is_active", true)
        .order("display_order", { ascending: true });

      if (error) throw error;

      setCategories(data || []);
      return data || [];
    } catch (error) {
      console.error("Error fetching categories:", error);
      setError("خطا در دریافت دسته‌بندی محصولات");
      return [];
    }
  };

  // Fetch brands from Supabase
  const fetchBrands = async () => {
    try {
      const { data, error } = await supabase
        .from("brands")
        .select("*")
        .order("name", { ascending: true });

      if (error) throw error;

      setBrands(data || []);
      return data || [];
    } catch (error) {
      console.error("Error fetching brands:", error);
      setError("خطا در دریافت برندها");
      return [];
    }
  };

  const queryProducts = useCallback(async ({
    page = 0,
    pageSize = PRODUCT_PAGE_SIZE,
    brandName = "all",
    searchTerm = "",
    sortBy = "newest",
  } = {}) => {
    const safePage = Math.max(0, Number(page) || 0);
    const safePageSize = Math.min(48, Math.max(1, Number(pageSize) || PRODUCT_PAGE_SIZE));
    const from = safePage * safePageSize;
    const to = from + safePageSize - 1;
    const search = sanitizeProductSearch(searchTerm);

    let query = supabase
      .from("products")
      .select("*", { count: "exact" })
      .eq("is_active", true);

    if (brandName && brandName !== "all") {
      query = query.eq("brand_name", brandName);
    }

    if (search) {
      const pattern = `%${search}%`;
      query = query.or(
        `name->>fa.ilike.${pattern},name->>en.ilike.${pattern},brand_name.ilike.${pattern}`,
      );
    }

    if (sortBy === "price-asc") {
      query = query.order("final_price_toman", { ascending: true, nullsFirst: false });
    } else if (sortBy === "price-desc") {
      query = query.order("final_price_toman", { ascending: false, nullsFirst: false });
    } else if (sortBy === "name") {
      query = query.order("name->>fa", { ascending: true });
    } else {
      query = query.order("created_at", { ascending: false });
    }

    const { data, error, count } = await query.range(from, to);
    if (error) throw error;

    return {
      products: normalizeProducts(data),
      count: Number(count ?? data?.length ?? 0),
    };
  }, []);

  // Backward-compatible bounded fetch for consumers that need a small catalog window.
  const fetchProducts = useCallback(async (options = {}) => {
    setLoading(true);
    try {
      const result = await queryProducts(options);
      setProducts(result.products);
      setLoading(false);
      return result.products;
    } catch (error) {
      console.error("Error fetching products:", error);
      setError("خطا در دریافت محصولات");
      setLoading(false);
      return [];
    }
  }, [queryProducts]);

  // Fetch featured products from Supabase
  const fetchFeaturedProducts = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from("products")
        .select("*")
        .eq("is_active", true)
        .eq("is_featured", true)
        .order("created_at", { ascending: false })
        .limit(8);

      if (error) throw error;

      const normalized = normalizeProducts(data);
      setFeaturedProducts(normalized);
      return normalized;
    } catch (error) {
      console.error("Error fetching featured products:", error);
      return [];
    }
  }, []);

  // Fetch a single product by ID
  const fetchProductById = async (productId) => {
    try {
      const { data, error } = await supabase
        .from("products")
        .select("*")
        .eq("id", productId)
        .single();

      if (error) {
        if (error.code === "PGRST116") {
          setError("محصول مورد نظر یافت نشد");
          return null;
        }
        throw error;
      }

      return normalizeProduct(data);
    } catch (error) {
      console.error("Error fetching product:", error);
      setError("خطا در دریافت اطلاعات محصول");
      return null;
    }
  };

  // Fetch products by category
  const fetchProductsByCategory = async (categoryId) => {
    setLoading(true);
    try {
      const category = categories.find(
        (item) => item.id === categoryId || item.slug === categoryId,
      );
      const categorySlug = category?.slug || categoryId;
      const { data, error } = await supabase
        .from("products")
        .select("*")
        .eq("category_slug", categorySlug)
        .eq("is_active", true)
        .order("created_at", { ascending: false });

      if (error) throw error;

      setLoading(false);
      return normalizeProducts(data);
    } catch (error) {
      console.error("Error fetching products by category:", error);
      setError("خطا در دریافت محصولات دسته‌بندی");
      setLoading(false);
      return [];
    }
  };

  // Fetch products by brand (supports both brand_id and brand_name)
  const fetchProductsByBrand = async (brandIdOrName) => {
    setLoading(true);
    try {
      // Check if it's a UUID (brand_id) or string (brand_name)
      const isUUID =
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
          brandIdOrName,
        );

      let query = supabase.from("products").select("*").eq("is_active", true);

      if (isUUID) {
        query = query.eq("brand_id", brandIdOrName);
      } else {
        query = query.eq("brand_name", brandIdOrName);
      }

      const { data, error } = await query.order("created_at", {
        ascending: false,
      });

      if (error) throw error;

      setLoading(false);
      return normalizeProducts(data);
    } catch (error) {
      console.error("Error fetching products by brand:", error);
      setError("خطا در دریافت محصولات برند");
      setLoading(false);
      return [];
    }
  };

  // Search products by term
  const searchProducts = async (searchTerm) => {
    setLoading(true);
    try {
      // Use Supabase text search with ilike for multiple fields
      const searchPattern = `%${searchTerm}%`;

      const { data, error } = await supabase
        .from("products")
        .select("*")
        .eq("is_active", true)
        .or(
          `name->>fa.ilike.${searchPattern},name->>en.ilike.${searchPattern},description->>fa.ilike.${searchPattern},brand_name.ilike.${searchPattern}`,
        )
        .order("created_at", { ascending: false })
        .limit(50);

      if (error) throw error;

      setLoading(false);
      return normalizeProducts(data);
    } catch (error) {
      console.error("Error searching products:", error);
      setError("خطا در جستجوی محصولات");
      setLoading(false);
      return [];
    }
  };

  // Load initial data
  useEffect(() => {
    const loadInitialData = async () => {
      setLoading(true);
      try {
        await Promise.all([
          fetchCategories(),
          fetchBrands(),
        ]);
        setError(null);
      } catch (error) {
        console.error("Error loading initial data:", error);
        setError("خطا در بارگذاری اطلاعات اولیه");
      }
      setLoading(false);
    };

    loadInitialData();
  }, []);

  const value = {
    products,
    categories,
    brands,
    featuredProducts,
    loading,
    error,
    fetchProducts,
    queryProducts,
    fetchCategories,
    fetchBrands,
    fetchFeaturedProducts,
    fetchProductById,
    fetchProductsByCategory,
    fetchProductsByBrand,
    searchProducts,
  };

  return (
    <ProductContext.Provider value={value}>{children}</ProductContext.Provider>
  );
};

export default ProductContext;
